import React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  CircleUserRound,
  FileText,
  FolderKanban,
  Home,
  KeyRound,
  MailCheck,
  MapPin,
  MessageSquareText,
  Power,
  PowerOff,
  Send,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  UserRound,
  X,
  XCircle,
  Copy,
  Check,
} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";
import "./AdminUserProfilePage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

function CopyableEmail({
  value,
  copyLabel = "Copy email address",
  copiedLabel = "Copied",
  showValue = true,
}) {
  const [copied, setCopied] = React.useState(false);

  const timerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  async function handleCopy(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch {
      const textarea = document.createElement("textarea");

      textarea.value = String(value);
      textarea.setAttribute("readonly", "");

      textarea.style.position = "fixed";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);

      textarea.select();

      document.execCommand("copy");

      document.body.removeChild(textarea);
    }

    setCopied(true);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  }

  if (!value) {
    return "—";
  }

  return (
    <span className="admin-user-profile__copyable-email">
      {showValue ? (
        <span className="admin-user-profile__email-value">{value}</span>
      ) : null}

      <button
        type="button"
        className={[
          "admin-user-profile__copy-email",
          copied ? "admin-user-profile__copy-email--copied" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleCopy}
        aria-label={copied ? copiedLabel : copyLabel}
        title={copied ? copiedLabel : copyLabel}
      >
        {copied ? (
          <Check size={14} strokeWidth={2.3} />
        ) : (
          <Copy size={14} strokeWidth={2} />
        )}
      </button>
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function locationLabel(countryCode, region) {
  const countries = {
    BE: "Belgium",
    ES: "Spain",
  };

  const regions = {
    FLANDERS: "Flanders",
    GIRONA: "Girona",
  };

  const country = countries[countryCode] || countryCode || "";
  const regionName = regions[region] || region || "";

  return [country, regionName].filter(Boolean).join(" / ");
}

function formatRole(value) {
  const roles = {
    LOAN_OFFICER: "Loan Officer",
    MANAGER: "Manager",
    STAFF: "Staff",
    ADMIN: "Administrator",
    UNDERWRITER: "Underwriter",
    AUDITOR: "Auditor",
  };

  return roles[value] || value || "—";
}

function formatOrganisationType(value) {
  const types = {
    BANK: "Bank",
    OSS: "One-Stop Shop",
    INVESTOR: "Investor",
    PLATFORM: "Platform Administration",
  };

  return types[value] || value || "—";
}

function getApiErrorMessage(payload, fallback) {
  if (!payload || typeof payload !== "object") return fallback;

  if (typeof payload.detail === "string" && payload.detail) {
    return payload.detail;
  }

  if (typeof payload.message === "string" && payload.message) {
    return payload.message;
  }

  for (const value of Object.values(payload)) {
    if (typeof value === "string" && value) {
      return value;
    }

    if (Array.isArray(value) && value.length) {
      return String(value[0]);
    }
  }

  return fallback;
}

function InfoItem({
  label,
  value,
  full = false,
  copyable = false,
  copyLabel = "Copy email address",
  copiedLabel = "Copied",
}) {
  return (
    <div
      className={`admin-user-profile__info-item ${
        full ? "admin-user-profile__info-item--full" : ""
      }`}
    >
      <span>{label}</span>

      <strong>
        {copyable ? (
          <CopyableEmail
            value={value}
            copyLabel={copyLabel}
            copiedLabel={copiedLabel}
          />
        ) : (
          valueOrDash(value)
        )}
      </strong>
    </div>
  );
}

function ActivityItem({ label, value, icon: Icon, description }) {
  return (
    <div className="admin-user-profile__activity-item">
      <div className="admin-user-profile__activity-top">
        <div className="admin-user-profile__activity-icon">
          <Icon size={19} />
        </div>

        <strong>{value ?? 0}</strong>
      </div>

      <div className="admin-user-profile__activity-copy">
        <span>{label}</span>
        {description ? <small>{description}</small> : null}
      </div>
    </div>
  );
}

function EditField({
  label,
  name,
  value,
  onChange = () => {},
  options = null,
  disabled = false,
  full = false,
  type = "text",
  copyable = false,
  copyLabel = "Copy email address",
  copiedLabel = "Copied",
}) {
  return (
    <label
      className={`admin-user-profile__edit-field ${
        full ? "admin-user-profile__edit-field--full" : ""
      }`}
    >
      <span>{label}</span>

      {options ? (
        <select
          name={name}
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <div
          className={[
            "admin-user-profile__edit-input-wrap",
            copyable ? "admin-user-profile__edit-input-wrap--copyable" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <input
            type={type}
            name={name}
            value={value ?? ""}
            onChange={onChange}
            disabled={disabled}
          />

          {copyable && value ? (
            <div className="admin-user-profile__edit-copy">
              <CopyableEmail
                value={value}
                showValue={false}
                copyLabel={copyLabel}
                copiedLabel={copiedLabel}
              />
            </div>
          ) : null}
        </div>
      )}
    </label>
  );
}

function getRoleOptions(actorType, currentRole) {
  let options = [];

  if (actorType === "LENDER") {
    options = [
      { value: "LOAN_OFFICER", label: "Loan Officer" },
      { value: "MANAGER", label: "Manager" },
      { value: "STAFF", label: "Staff" },
      { value: "ADMIN", label: "Administrator" },
    ];
  } else {
    options = [
      { value: "MANAGER", label: "Manager" },
      { value: "STAFF", label: "Staff" },
      { value: "ADMIN", label: "Administrator" },
    ];
  }

  if (currentRole && !options.some((option) => option.value === currentRole)) {
    options.unshift({
      value: currentRole,
      label: formatRole(currentRole),
    });
  }

  return options;
}

function AdminConfirmModal({
  modal,
  loading,
  onClose,
  onConfirm,
  deleteConfirmation,
  onDeleteConfirmationChange,
  t,
}) {
  React.useEffect(() => {
    if (!modal.open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modal.open, loading, onClose]);

  if (!modal.open) return null;

  let Icon = ShieldCheck;

  if (modal.type === "PASSWORD_RESET") Icon = KeyRound;
  if (modal.type === "EMAIL_VERIFICATION") Icon = MailCheck;
  if (modal.type === "DEACTIVATE") Icon = TriangleAlert;
  if (modal.type === "ACTIVATE") Icon = Power;
  if (modal.type === "DELETE_ACCOUNT") Icon = Trash2;

  return createPortal(
    <div
      className={`admin-user-modal__backdrop ${
        modal.closing ? "admin-user-modal__backdrop--closing" : ""
      }`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        className={`admin-user-modal__dialog ${
          modal.closing ? "admin-user-modal__dialog--closing" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-user-modal-title"
      >
        <button
          type="button"
          className="admin-user-modal__close"
          onClick={onClose}
          disabled={loading}
          aria-label={t("modal.close")}
        >
          <X size={22} />
        </button>

        <div
          className={`admin-user-modal__icon admin-user-modal__icon--${modal.tone}`}
        >
          <Icon size={27} />
        </div>

        <h2 id="admin-user-modal-title">{modal.title}</h2>

        <p className="admin-user-modal__message">{modal.message}</p>

        {modal.detail ? (
          <div className="admin-user-modal__detail">
            {modal.detailType === "email" ? (
              <CopyableEmail
                value={modal.detail}
                copyLabel={t("actions.copyEmail", "Copy email address")}
                copiedLabel={t("actions.emailCopied", "Copied")}
              />
            ) : (
              modal.detail
            )}
          </div>
        ) : null}

        {modal.warnings?.length > 0 ? (
          <div className="admin-user-modal__warnings">
            <div className="admin-user-modal__warnings-heading">
              <TriangleAlert size={18} />

              <strong>
                {t("modal.deleteWarningsTitle", "Linked platform data")}
              </strong>
            </div>

            <p>
              {t(
                "modal.deleteWarningsIntro",
                "This account is linked to the following platform records:",
              )}
            </p>

            <ul>
              {modal.warnings.map((warning) => (
                <li key={warning.key}>
                  <strong>{warning.value}</strong>
                  <span>{warning.label}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {modal.requireDeleteConfirmation ? (
          <div className="admin-user-modal__delete-confirmation">
            <label htmlFor="admin-delete-confirmation">
              {t(
                "modal.deleteConfirmationLabel",
                "Type DELETE to permanently remove this account",
              )}
            </label>

            <input
              id="admin-delete-confirmation"
              type="text"
              value={deleteConfirmation}
              onChange={(event) =>
                onDeleteConfirmationChange(event.target.value)
              }
              disabled={loading}
              autoComplete="off"
              placeholder="DELETE"
            />
          </div>
        ) : null}

        <div className="admin-user-modal__actions">
          <button
            type="button"
            className="admin-user-modal__button admin-user-modal__button--cancel"
            onClick={onClose}
            disabled={loading}
          >
            {t("modal.cancel")}
          </button>

          <button
            type="button"
            className={`admin-user-modal__button admin-user-modal__button--confirm admin-user-modal__button--${modal.tone}`}
            onClick={onConfirm}
            disabled={
              loading ||
              (modal.requireDeleteConfirmation &&
                deleteConfirmation !== "DELETE")
            }
          >
            {loading ? t("modal.processing") : modal.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function scrollToPageTop() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
}

export default function AdminUserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCategory = location.state?.fromCategory;
  const usersListPath = fromCategory
    ? `/admin/users?category=${fromCategory}`
    : "/admin/users";

  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation("admin-user-profile");
  const { showToast } = useToast();
  const goBackToUsers = () => {
    if (fromCategory) {
      navigate(-1);
    } else {
      navigate(usersListPath);
    }
  };
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [adminActionLoading, setAdminActionLoading] = React.useState("");
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isSavingProfile, setIsSavingProfile] = React.useState(false);

  const [editForm, setEditForm] = React.useState({
    first_name: "",
    last_name: "",
    phone: "",
    country_of_residence: "",
    country_of_property_purchase: "",
    preferred_language: "",
    org_role: "",
  });

  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    closing: false,
    type: "",
    tone: "normal",
    title: "",
    message: "",
    detail: "",
    detailType: "",
    warnings: [],
    requireDeleteConfirmation: false,
    confirmLabel: "",
  });

  const [deleteConfirmation, setDeleteConfirmation] = React.useState("");

  const modalCloseTimer = React.useRef(null);

  React.useEffect(() => {
    const controller = new AbortController();

    async function fetchUser() {
      setLoading(true);
      setError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE_URL}/api/admin/users/${userId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));

          throw new Error(getApiErrorMessage(payload, t("error")));
        }

        const payload = await response.json();

        if (!controller.signal.aborted) {
          setData(payload);
        }
      } catch (err) {
        if (err.name !== "AbortError" && !controller.signal.aborted) {
          setError(err.message || t("error"));
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchUser();

    return () => controller.abort();
  }, [getAccessTokenSilently, t, userId]);

  React.useEffect(() => {
    return () => {
      if (modalCloseTimer.current) {
        clearTimeout(modalCloseTimer.current);
      }
    };
  }, []);

  const closeConfirmModal = React.useCallback(() => {
    setConfirmModal((current) => {
      if (!current.open || current.closing) {
        return current;
      }

      return {
        ...current,
        closing: true,
      };
    });

    if (modalCloseTimer.current) {
      clearTimeout(modalCloseTimer.current);
    }

    modalCloseTimer.current = setTimeout(() => {
      setConfirmModal({
        open: false,
        closing: false,
        type: "",
        tone: "normal",
        title: "",
        message: "",
        detail: "",
        warnings: [],
        requireDeleteConfirmation: false,
        confirmLabel: "",
        detailType: "",
      });

      setDeleteConfirmation("");
    }, 220);
  }, []);

  function openConfirmModal({
    type,
    tone = "normal",
    title,
    message,
    detail = "",
    detailType = "",
    warnings = [],
    requireDeleteConfirmation = false,
    confirmLabel,
  }) {
    if (modalCloseTimer.current) {
      clearTimeout(modalCloseTimer.current);
    }

    setDeleteConfirmation("");

    setConfirmModal({
      open: true,
      closing: false,
      type,
      tone,
      title,
      message,
      detail,
      detailType,
      warnings,
      requireDeleteConfirmation,
      confirmLabel,
    });
  }

  if (loading) {
    return (
      <main className="admin-user-profile">
        <div className="admin-user-profile__loading">
          <span className="admin-user-profile__spinner" />
          <p>{t("loading")}</p>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="admin-user-profile">
        <button
          type="button"
          className="admin-user-profile__back"
          onClick={goBackToUsers}
        >
          <ArrowLeft size={16} />
          {t("back")}
        </button>

        <div className="admin-user-profile__error">{error || t("error")}</div>
      </main>
    );
  }

  const {
    user,
    authentication,
    personal_information: personal = {},
    organisation,
    homebuyer_context: hb,
    activity = {},
    deletion_preview: deletionPreview = {},
  } = data;

  const actorLabel =
    {
      HOMEOWNER: t("actors.homeowner"),
      LENDER: t("actors.lender"),
      OSS: t("actors.oss"),
      INVESTOR: t("actors.investor"),
    }[user.actor_type] || user.actor_type;

  const cleanActorLabel = actorLabel.replace(/\s+profile$/i, "");
  const userLocation = locationLabel(user.country_code, user.region);

  const personalRole =
    user.actor_type === "HOMEOWNER"
      ? cleanActorLabel
      : organisation
        ? formatRole(organisation.org_role)
        : cleanActorLabel;

  const roleOptions = getRoleOptions(user.actor_type, editForm.org_role);

  const activityItems = (() => {
    if (user.actor_type === "HOMEOWNER") {
      return [
        {
          label: t("activity.dossiers"),
          value: activity.dossiers,
          icon: FileText,
          description: t("activity.dossiersHint"),
        },
        {
          label: t("activity.loanCases"),
          value: activity.loan_cases,
          icon: FolderKanban,
          description: t("activity.loanCasesHint"),
        },
        {
          label: t("activity.projects"),
          value: activity.projects,
          icon: Home,
          description: t("activity.projectsHint"),
        },
      ];
    }

    if (user.actor_type === "LENDER") {
      return [
        {
          label: t("activity.organisationCases"),
          value: activity.loan_cases,
          icon: FolderKanban,
          description: t("activity.organisationCasesHint"),
        },
        {
          label: t("activity.threads"),
          value: activity.communication_threads,
          icon: MessageSquareText,
          description: t("activity.threadsHint"),
        },
        {
          label: t("activity.messages"),
          value: activity.messages_sent,
          icon: Send,
          description: t("activity.messagesHint"),
        },
      ];
    }

    if (user.actor_type === "OSS") {
      return [
        {
          label: t("activity.projects"),
          value: activity.projects,
          icon: Home,
          description: t("activity.projectsHint"),
        },
        {
          label: t("activity.requests"),
          value: activity.oss_requests,
          icon: FileText,
          description: t("activity.requestsHint"),
        },
        {
          label: t("activity.threads"),
          value: activity.communication_threads,
          icon: MessageSquareText,
          description: t("activity.threadsHint"),
        },
      ];
    }

    return [
      {
        label: t("activity.threads"),
        value: activity.communication_threads,
        icon: MessageSquareText,
        description: t("activity.threadsHint"),
      },
      {
        label: t("activity.messages"),
        value: activity.messages_sent,
        icon: Send,
        description: t("activity.messagesHint"),
      },
    ];
  })();

  const deletionWarnings = [
    {
      key: "dossiers",
      value: deletionPreview.dossiers || 0,
      label: t("delete.dossiers", "linked dossiers"),
    },
    {
      key: "loan_cases",
      value: deletionPreview.loan_cases || 0,
      label: t("delete.loanCases", "linked loan cases"),
    },
    {
      key: "properties",
      value: deletionPreview.properties || 0,
      label: t("delete.properties", "linked properties"),
    },
    {
      key: "projects",
      value: deletionPreview.projects || 0,
      label: t("delete.projects", "renovation projects"),
    },
    {
      key: "oss_requests",
      value: deletionPreview.oss_requests || 0,
      label: t("delete.ossRequests", "OSS requests"),
    },
    {
      key: "communication_threads",
      value: deletionPreview.communication_threads || 0,
      label: t("delete.communicationThreads", "communication threads"),
    },
    {
      key: "messages",
      value: deletionPreview.messages || 0,
      label: t("delete.messages", "messages"),
    },
    {
      key: "role_assignments",
      value: deletionPreview.role_assignments || 0,
      label: t("delete.roleAssignments", "role assignments"),
    },
  ].filter((item) => item.value > 0);

  function handleStartEdit() {
    setEditForm({
      first_name: personal.first_name || user.first_name || "",
      last_name: personal.last_name || user.last_name || "",
      phone: personal.phone || "",
      country_of_residence: personal.country_of_residence || "",
      country_of_property_purchase:
        personal.country_of_property_purchase ||
        (user.actor_type === "HOMEOWNER" ? user.country_code || "" : ""),
      preferred_language: personal.preferred_language || "",
      org_role: organisation?.org_role || "",
    });

    setIsEditMode(true);
  }

  function handleCancelEdit() {
    if (isSavingProfile) return;
    setIsEditMode(false);
  }

  function handleEditChange(event) {
    const { name, value } = event.target;

    setEditForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleSaveProfile() {
    if (isSavingProfile) return;

    const firstName = editForm.first_name.trim();
    const lastName = editForm.last_name.trim();

    if (!firstName || !lastName) {
      showToast({
        type: "error",
        title: t("toast.validationTitle", "Check the form"),
        message: t(
          "edit.nameRequired",
          "First name and last name are required.",
        ),
      });

      return;
    }

    const body = {
      first_name: firstName,
      last_name: lastName,
    };

    if (user.actor_type === "HOMEOWNER") {
      body.homeowner_profile = {
        phone: editForm.phone.trim(),
        country_of_residence: editForm.country_of_residence,
        country_of_property_purchase: editForm.country_of_property_purchase,
        preferred_language: editForm.preferred_language,
      };
    } else if (organisation && editForm.org_role) {
      body.org_role = editForm.org_role;
    }

    try {
      setIsSavingProfile(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getApiErrorMessage(
            payload,
            t("edit.saveError", "The profile could not be updated."),
          ),
        );
      }

      let updatedData =
        payload?.user && payload?.personal_information ? payload : null;

      if (!updatedData) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/admin/users/${userId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (refreshResponse.ok) {
          updatedData = await refreshResponse.json();
        }
      }

      if (updatedData) {
        setData(updatedData);
      } else {
        setData((current) => {
          const updatedPersonal = {
            ...current.personal_information,
            first_name: firstName,
            last_name: lastName,
          };

          let updatedOrganisation = current.organisation;

          if (user.actor_type === "HOMEOWNER") {
            Object.assign(updatedPersonal, {
              phone: editForm.phone.trim(),
              country_of_residence: editForm.country_of_residence,
              country_of_property_purchase:
                editForm.country_of_property_purchase,
              preferred_language: editForm.preferred_language,
            });
          } else if (updatedOrganisation && editForm.org_role) {
            updatedOrganisation = {
              ...updatedOrganisation,
              org_role: editForm.org_role,
            };
          }

          const displayName = `${firstName} ${lastName}`.trim();
          const initials =
            `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
            current.user.initials;

          return {
            ...current,
            user: {
              ...current.user,
              first_name: firstName,
              last_name: lastName,
              display_name: displayName,
              initials,
            },
            personal_information: updatedPersonal,
            organisation: updatedOrganisation,
          };
        });
      }

      setIsEditMode(false);

      showToast({
        type: "success",
        title: t("toast.profileSavedTitle", "Profile updated"),
        message: t(
          "toast.profileSavedMessage",
          "The user profile was updated successfully.",
        ),
      });
      scrollToPageTop();
    } catch (err) {
      console.error("Failed to update admin-managed user:", err);

      showToast({
        type: "error",
        title: t("toast.errorTitle", "Something went wrong"),
        message:
          err.message ||
          t("edit.saveError", "The profile could not be updated."),
      });
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function runAdminAction({
    action,
    url,
    method = "POST",
    body = null,
    successTitle,
    successMessage,
  }) {
    if (adminActionLoading) return null;

    setAdminActionLoading(action);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE_URL}${url}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getApiErrorMessage(
            payload,
            t("admin.actionError", "The action could not be completed."),
          ),
        );
      }

      showToast({
        type: "success",
        title: successTitle,
        message: payload.message || successMessage,
      });

      setTimeout(() => {
        scrollToPageTop();
      }, 260);

      return payload;
    } catch (err) {
      console.error(`Admin action ${action} failed:`, err);

      showToast({
        type: "error",
        title: t("toast.errorTitle", "Something went wrong"),
        message:
          err.message ||
          t("admin.actionError", "The action could not be completed."),
      });

      return null;
    } finally {
      setAdminActionLoading("");
    }
  }

  function handlePasswordReset() {
    openConfirmModal({
      type: "PASSWORD_RESET",
      tone: "normal",
      title: t("modal.passwordResetTitle"),
      message: t("modal.passwordResetMessage"),
      detail: user.email,
      detailType: "email",
      confirmLabel: t("modal.sendReset"),
    });
  }

  function handleResendVerification() {
    openConfirmModal({
      type: "EMAIL_VERIFICATION",
      tone: "normal",
      title: t("modal.verificationTitle"),
      message: t("modal.verificationMessage"),
      detail: user.email,
      detailType: "email",
      confirmLabel: t("modal.sendVerification"),
    });
  }

  function handleAccountStatus() {
    if (user.is_active) {
      openConfirmModal({
        type: "DEACTIVATE",
        tone: "warning",
        title: t("modal.deactivateTitle"),
        message: t("modal.deactivateMessage"),
        detail: user.email,
        detailType: "email",
        confirmLabel: t("modal.deactivateButton"),
      });

      return;
    }

    openConfirmModal({
      type: "ACTIVATE",
      tone: "success",
      title: t("modal.activateTitle"),
      message: t("modal.activateMessage"),
      detail: user.email,
      detailType: "email",
      confirmLabel: t("modal.activateButton"),
    });
  }

  async function handleConfirmModal() {
    const action = confirmModal.type;

    if (action === "DELETE_ACCOUNT") {
      if (deleteConfirmation !== "DELETE") {
        return;
      }

      const payload = await runAdminAction({
        action: "DELETE_ACCOUNT",

        url: `/api/admin/users/${userId}/`,

        method: "DELETE",

        body: {
          confirmation: deleteConfirmation,
        },

        successTitle: t(
          "toast.accountDeletedTitle",
          "Account permanently deleted",
        ),

        successMessage: t(
          "toast.accountDeletedMessage",
          "The user account was permanently removed from LEG-UP.",
        ),
      });

      if (!payload) {
        return;
      }

      closeConfirmModal();

      setTimeout(() => {
        navigate(usersListPath, {
          replace: true,
        });
      }, 250);

      return;
    }

    if (action === "PASSWORD_RESET") {
      const payload = await runAdminAction({
        action: "PASSWORD_RESET",
        url: `/api/admin/users/${userId}/password-reset/`,
        successTitle: t(
          "toast.passwordResetTitle",
          "Password reset email sent",
        ),
        successMessage: t("admin.passwordResetSuccess"),
      });

      if (payload) {
        closeConfirmModal();
      }

      return;
    }

    if (action === "EMAIL_VERIFICATION") {
      const payload = await runAdminAction({
        action: "EMAIL_VERIFICATION",
        url: `/api/admin/users/${userId}/resend-verification/`,
        successTitle: t("toast.verificationTitle", "Verification email sent"),
        successMessage: t("admin.verificationSuccess"),
      });

      if (payload) {
        closeConfirmModal();
      }

      return;
    }

    if (action === "DEACTIVATE" || action === "ACTIVATE") {
      const shouldBeActive = action === "ACTIVATE";

      const payload = await runAdminAction({
        action,
        url: `/api/admin/users/${userId}/status/`,
        method: "PATCH",
        body: {
          is_active: shouldBeActive,
        },
        successTitle: t("toast.accountUpdatedTitle", "Account updated"),
        successMessage: shouldBeActive
          ? t("admin.activateSuccess")
          : t("admin.deactivateSuccess"),
      });

      if (!payload) return;

      setData((current) => ({
        ...current,
        user: {
          ...current.user,
          is_active:
            typeof payload.is_active === "boolean"
              ? payload.is_active
              : shouldBeActive,
        },
      }));

      closeConfirmModal();
    }
  }

  function handleDeleteAccount() {
    openConfirmModal({
      type: "DELETE_ACCOUNT",
      tone: "danger",

      title: t("modal.deleteAccountTitle", "Permanently delete account?"),

      message: t(
        "modal.deleteAccountMessage",
        "This permanently removes the user's LEG-UP account and access. This action cannot be undone.",
      ),

      detail: user.email || user.display_name,

      warnings: deletionWarnings,

      requireDeleteConfirmation: true,

      confirmLabel: t(
        "modal.deleteAccountButton",
        "Permanently delete account",
      ),
    });
  }

  return (
    <main className="admin-user-profile">
      {/* =====================================================
          PAGE HEADER
          ===================================================== */}

      <div className="admin-user-profile__header">
        <div>
          <button
            type="button"
            className="admin-user-profile__back"
            onClick={goBackToUsers}
            disabled={isSavingProfile}
          >
            <ArrowLeft size={16} />
            {t("back")}
          </button>

          <h1>{t("title")}</h1>
        </div>

        {userLocation ? (
          <div className="admin-user-profile__location-pill">
            {userLocation}
          </div>
        ) : null}
      </div>

      {/* =====================================================
          PROFILE HEADER
          ===================================================== */}

      <section className="admin-user-profile__hero">
        <div
          className={`admin-user-profile__avatar ${
            user.avatar_url ? "admin-user-profile__avatar--image" : ""
          }`}
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.display_name} />
          ) : (
            <span>{user.initials}</span>
          )}
        </div>

        <div className="admin-user-profile__identity">
          <div className="admin-user-profile__name-line">
            <h2>{user.display_name}</h2>

            <span
              className={`admin-user-profile__status ${
                user.is_active
                  ? "admin-user-profile__status--active"
                  : "admin-user-profile__status--inactive"
              }`}
            >
              {user.is_active ? (
                <CheckCircle2 size={13} />
              ) : (
                <XCircle size={13} />
              )}

              {user.is_active ? t("active") : t("inactive")}
            </span>
          </div>

          <p className="admin-user-profile__identity-line">
            {actorLabel}

            <span>•</span>

            <CopyableEmail
              value={user.email}
              copyLabel={t("actions.copyEmail", "Copy email address")}
              copiedLabel={t("actions.emailCopied", "Copied")}
            />
          </p>

          <div className="admin-user-profile__identity-meta">
            {userLocation ? (
              <span>
                <MapPin size={14} />
                {userLocation}
              </span>
            ) : null}

            {hb?.loan_case?.case_reference ? (
              <span>
                <FileText size={14} />
                {hb.loan_case.case_reference}
              </span>
            ) : null}

            {organisation?.name ? (
              <span>
                <Building2 size={14} />
                {organisation.name}
              </span>
            ) : null}
          </div>
        </div>

        <div className="admin-user-profile__hero-actions">
          {isEditMode ? (
            <>
              <button
                type="button"
                className="admin-user-profile__action admin-user-profile__action--secondary"
                onClick={handleCancelEdit}
                disabled={isSavingProfile}
              >
                {t("actions.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="admin-user-profile__action admin-user-profile__action--primary"
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
              >
                {isSavingProfile
                  ? t("actions.saving", "Saving...")
                  : t("actions.save", "Save changes")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="admin-user-profile__action admin-user-profile__action--primary"
                onClick={handleStartEdit}
                disabled={Boolean(adminActionLoading)}
              >
                {t("actions.edit")}
              </button>

              <button
                type="button"
                className="admin-user-profile__action admin-user-profile__action--danger"
                onClick={handleDeleteAccount}
                disabled={
                  Boolean(adminActionLoading) || isEditMode || isSavingProfile
                }
              >
                <Trash2 size={16} strokeWidth={2} />

                {t("actions.delete")}
              </button>
            </>
          )}
        </div>
      </section>

      {/* =====================================================
          2 x 2 GRID
          ===================================================== */}

      <div className="admin-user-profile__grid">
        {/* =================================================
            PERSONAL INFORMATION
            ================================================= */}

        <section
          className={`admin-user-profile__card ${
            isEditMode ? "admin-user-profile__card--editing" : ""
          }`}
        >
          <header className="admin-user-profile__card-header">
            <div className="admin-user-profile__card-heading">
              <div className="admin-user-profile__card-icon">
                <UserRound size={18} />
              </div>

              <h3>{t("personal.title")}</h3>
            </div>

            <span>
              {isEditMode
                ? t(
                    "edit.personalSubtitle",
                    "Update the user's account information.",
                  )
                : t("personal.subtitle")}
            </span>
          </header>

          <div className="admin-user-profile__card-body">
            {isEditMode ? (
              <div className="admin-user-profile__edit-grid">
                <EditField
                  label={t("personal.firstName", "First name")}
                  name="first_name"
                  value={editForm.first_name}
                  onChange={handleEditChange}
                />

                <EditField
                  label={t("personal.lastName", "Last name")}
                  name="last_name"
                  value={editForm.last_name}
                  onChange={handleEditChange}
                />

                <EditField
                  label={t("personal.email")}
                  name="email"
                  value={personal.email || user.email || ""}
                  disabled
                  copyable
                  copyLabel={t("actions.copyEmail", "Copy email address")}
                  copiedLabel={t("actions.emailCopied", "Copied")}
                />

                {user.actor_type === "HOMEOWNER" ? (
                  <>
                    <EditField
                      label={t("personal.phone")}
                      name="phone"
                      value={editForm.phone}
                      onChange={handleEditChange}
                      type="tel"
                    />

                    <EditField
                      label={t(
                        "personal.residenceCountry",
                        "Country of residence",
                      )}
                      name="country_of_residence"
                      value={editForm.country_of_residence}
                      onChange={handleEditChange}
                      options={[
                        {
                          value: "",
                          label: t("edit.selectCountry", "Select country"),
                        },
                        {
                          value: "BE",
                          label: t("countries.BE", "Belgium"),
                        },
                        {
                          value: "ES",
                          label: t("countries.ES", "Spain"),
                        },
                      ]}
                    />

                    <EditField
                      label={t(
                        "personal.purchaseCountry",
                        "Property purchase country",
                      )}
                      name="country_of_property_purchase"
                      value={editForm.country_of_property_purchase}
                      onChange={handleEditChange}
                      options={[
                        {
                          value: "",
                          label: t("edit.selectCountry", "Select country"),
                        },
                        {
                          value: "BE",
                          label: t("countries.BE", "Belgium"),
                        },
                        {
                          value: "ES",
                          label: t("countries.ES", "Spain"),
                        },
                      ]}
                    />

                    <EditField
                      label={t("personal.language", "Preferred language")}
                      name="preferred_language"
                      value={editForm.preferred_language}
                      onChange={handleEditChange}
                      options={[
                        {
                          value: "",
                          label: t("edit.selectLanguage", "Select language"),
                        },
                        {
                          value: "en",
                          label: "English",
                        },
                        {
                          value: "es",
                          label: "Español",
                        },
                        {
                          value: "ca",
                          label: "Català",
                        },
                        {
                          value: "nl",
                          label: "Nederlands",
                        },
                      ]}
                    />

                    <EditField
                      label={t("personal.role")}
                      name="actor_role"
                      value={personalRole}
                      disabled
                    />

                    <EditField
                      label={t("personal.status")}
                      name="account_status"
                      value={user.is_active ? t("active") : t("inactive")}
                      disabled
                    />
                  </>
                ) : (
                  <>
                    <EditField
                      label={t("personal.countryRegion")}
                      name="country_region"
                      value={userLocation}
                      disabled
                    />

                    <EditField
                      label={t("personal.role")}
                      name="org_role"
                      value={editForm.org_role}
                      onChange={handleEditChange}
                      options={roleOptions}
                    />

                    <EditField
                      label={t("personal.status")}
                      name="account_status"
                      value={user.is_active ? t("active") : t("inactive")}
                      disabled
                    />

                    <EditField
                      label={t("admin.emailVerified")}
                      name="email_verified"
                      value={
                        authentication?.email_verified
                          ? t("common.yes")
                          : t("common.no")
                      }
                      disabled
                    />
                  </>
                )}
              </div>
            ) : (
              <div className="admin-user-profile__info-grid">
                <InfoItem
                  label={t("personal.fullName")}
                  value={[personal.first_name, personal.last_name]
                    .filter(Boolean)
                    .join(" ")}
                />

                <InfoItem
                  label={t("personal.email")}
                  value={personal.email || user.email}
                  copyable
                  copyLabel={t("actions.copyEmail", "Copy email address")}
                  copiedLabel={t("actions.emailCopied", "Copied")}
                />

                {user.actor_type === "HOMEOWNER" ? (
                  <InfoItem
                    label={t("personal.phone")}
                    value={personal.phone}
                  />
                ) : (
                  <InfoItem
                    label={t("personal.countryRegion")}
                    value={userLocation}
                  />
                )}

                {user.actor_type === "HOMEOWNER" ? (
                  <InfoItem
                    label={t("personal.countryRegion")}
                    value={userLocation}
                  />
                ) : (
                  <InfoItem label={t("personal.role")} value={personalRole} />
                )}

                {user.actor_type === "HOMEOWNER" ? (
                  <InfoItem label={t("personal.role")} value={personalRole} />
                ) : (
                  <InfoItem
                    label={t("personal.status")}
                    value={user.is_active ? t("active") : t("inactive")}
                  />
                )}

                {user.actor_type === "HOMEOWNER" ? (
                  <InfoItem
                    label={t("personal.status")}
                    value={user.is_active ? t("active") : t("inactive")}
                  />
                ) : (
                  <InfoItem
                    label={t("admin.emailVerified")}
                    value={
                      authentication?.email_verified
                        ? t("common.yes")
                        : t("common.no")
                    }
                  />
                )}
              </div>
            )}
          </div>
        </section>

        {/* =================================================
            PROPERTY / ORGANISATION INFORMATION
            ================================================= */}

        {user.actor_type === "HOMEOWNER" ? (
          <section className="admin-user-profile__card">
            <header className="admin-user-profile__card-header">
              <div className="admin-user-profile__card-heading">
                <div className="admin-user-profile__card-icon">
                  <Home size={18} />
                </div>

                <h3>{t("case.title")}</h3>
              </div>

              <span>{t("case.subtitle")}</span>
            </header>

            <div className="admin-user-profile__card-body">
              <div className="admin-user-profile__info-grid">
                <InfoItem
                  full
                  label={t("case.propertyAddress")}
                  value={hb?.property?.full_address || t("case.noProperty")}
                />

                <InfoItem
                  label={t("case.loanCase")}
                  value={hb?.loan_case?.case_reference || t("case.noLoanCase")}
                />

                <InfoItem
                  label={t("case.dossier")}
                  value={hb?.dossier?.code || t("case.noDossier")}
                />

                <InfoItem
                  label={t("case.lender")}
                  value={hb?.loan_case?.lender_name}
                />

                <InfoItem
                  label={t("case.oss")}
                  value={hb?.oss_connection?.oss_name || t("case.noOss")}
                />
              </div>
            </div>
          </section>
        ) : (
          <section className="admin-user-profile__card">
            <header className="admin-user-profile__card-header">
              <div className="admin-user-profile__card-heading">
                <div className="admin-user-profile__card-icon">
                  <Building2 size={18} />
                </div>

                <h3>{t("organisation.title")}</h3>
              </div>

              <span>{t("organisation.subtitle")}</span>
            </header>

            <div className="admin-user-profile__card-body">
              {organisation ? (
                <div className="admin-user-profile__info-grid">
                  <InfoItem
                    full
                    label={t("organisation.name")}
                    value={organisation.name}
                  />

                  <InfoItem
                    label={t("organisation.role")}
                    value={formatRole(organisation.org_role)}
                  />

                  <InfoItem
                    label={t("organisation.type")}
                    value={formatOrganisationType(organisation.type)}
                  />

                  <InfoItem
                    label={t("organisation.vat")}
                    value={organisation.vat_number}
                  />

                  <InfoItem
                    label={t("organisation.country")}
                    value={locationLabel(
                      organisation.country_code,
                      organisation.region,
                    )}
                  />

                  {organisation.oss_profile ? (
                    <>
                      <InfoItem
                        label={t("organisation.contactPhone")}
                        value={organisation.oss_profile.contact_phone}
                      />

                      <InfoItem
                        label={t("organisation.website")}
                        value={organisation.oss_profile.website_url}
                      />
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="admin-user-profile__empty">—</div>
              )}
            </div>
          </section>
        )}

        {/* =================================================
            ACTIVITY SUMMARY
            ================================================= */}

        <section className="admin-user-profile__card">
          <header className="admin-user-profile__card-header">
            <div className="admin-user-profile__card-heading">
              <div className="admin-user-profile__card-icon">
                <CircleUserRound size={18} />
              </div>

              <h3>{t("activity.title")}</h3>
            </div>

            <span>{t("activity.subtitle")}</span>
          </header>

          <div className="admin-user-profile__card-body">
            <div className="admin-user-profile__activity-grid">
              {activityItems.map((item) => (
                <ActivityItem
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  icon={item.icon}
                  description={item.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* =================================================
            ADMIN CONTROLS
            ================================================= */}

        <section className="admin-user-profile__card">
          <header className="admin-user-profile__card-header">
            <div className="admin-user-profile__card-heading">
              <div className="admin-user-profile__card-icon">
                <ShieldCheck size={18} />
              </div>

              <h3>{t("admin.title")}</h3>
            </div>

            <span>{t("admin.subtitle")}</span>
          </header>

          <div className="admin-user-profile__card-body">
            <div className="admin-user-profile__info-grid">
              <InfoItem
                label={t("admin.lastLogin")}
                value={formatDateTime(authentication?.last_login_at)}
              />

              <InfoItem
                label={t("admin.created")}
                value={formatDate(user.created_at)}
              />

              <InfoItem
                label={t("admin.updated")}
                value={formatDate(user.updated_at)}
              />

              <InfoItem
                label={t("personal.status")}
                value={user.is_active ? t("active") : t("inactive")}
              />
            </div>

            <div className="admin-user-profile__admin-actions">
              <div className="admin-user-profile__admin-actions-heading">
                <span>{t("admin.accountActions")}</span>
                <small>{t("admin.accountActionsHint")}</small>
              </div>

              <div className="admin-user-profile__admin-action-grid">
                <button
                  type="button"
                  className="admin-user-profile__control-button"
                  disabled={
                    Boolean(adminActionLoading) || isEditMode || isSavingProfile
                  }
                  onClick={handlePasswordReset}
                >
                  <span className="admin-user-profile__control-icon">
                    <KeyRound size={17} />
                  </span>

                  <span>
                    <strong>
                      {adminActionLoading === "PASSWORD_RESET"
                        ? t("admin.sending")
                        : t("admin.passwordReset")}
                    </strong>

                    <small>{t("admin.passwordResetHint")}</small>
                  </span>
                </button>

                {!authentication?.email_verified ? (
                  <button
                    type="button"
                    className="admin-user-profile__control-button"
                    disabled={
                      Boolean(adminActionLoading) ||
                      isEditMode ||
                      isSavingProfile
                    }
                    onClick={handleResendVerification}
                  >
                    <span className="admin-user-profile__control-icon">
                      <MailCheck size={17} />
                    </span>

                    <span>
                      <strong>
                        {adminActionLoading === "EMAIL_VERIFICATION"
                          ? t("admin.sending")
                          : t("admin.resendVerification")}
                      </strong>

                      <small>{t("admin.resendVerificationHint")}</small>
                    </span>
                  </button>
                ) : (
                  <div className="admin-user-profile__control-button admin-user-profile__control-button--disabled">
                    <span className="admin-user-profile__control-icon admin-user-profile__control-icon--success">
                      <CheckCircle2 size={17} />
                    </span>

                    <span>
                      <strong>{t("admin.emailAlreadyVerified")}</strong>

                      <small>{t("admin.emailAlreadyVerifiedHint")}</small>
                    </span>
                  </div>
                )}

                <button
                  type="button"
                  className={`admin-user-profile__control-button ${
                    user.is_active
                      ? "admin-user-profile__control-button--warning"
                      : "admin-user-profile__control-button--activate"
                  }`}
                  disabled={
                    Boolean(adminActionLoading) || isEditMode || isSavingProfile
                  }
                  onClick={handleAccountStatus}
                >
                  <span className="admin-user-profile__control-icon">
                    {user.is_active ? (
                      <PowerOff size={17} />
                    ) : (
                      <Power size={17} />
                    )}
                  </span>

                  <span>
                    <strong>
                      {adminActionLoading === "DEACTIVATE" ||
                      adminActionLoading === "ACTIVATE"
                        ? t("admin.updating")
                        : user.is_active
                          ? t("admin.deactivate")
                          : t("admin.activate")}
                    </strong>

                    <small>
                      {user.is_active
                        ? t("admin.deactivateHint")
                        : t("admin.activateHint")}
                    </small>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <AdminConfirmModal
        modal={confirmModal}
        loading={Boolean(adminActionLoading)}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmModal}
        deleteConfirmation={deleteConfirmation}
        onDeleteConfirmationChange={setDeleteConfirmation}
        t={t}
      />
    </main>
  );
}
